import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { toast } from 'react-hot-toast';
import { authService } from '@/services/authService';
import { teamUserService } from '@/services/teamUserService';
import { clearAuthCookiesService } from '@/services/clearAuthCookiesService';
import { CookieUtils } from '@/utils/cookies';
import type {
  User,
  LoginCredentials,
  RegisterCredentials,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  ChangePasswordRequest,
  UpdateProfileRequest,
  AuthResponse,
} from '@/types/auth';

// Auth state interface
interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  loggedOut: boolean;
}

// Initial state
const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  loading: true,
  error: null,
  loggedOut: false,
};

// Async thunks
export const loginUser = createAsyncThunk(
  'auth/login',
  async (credentials: LoginCredentials, { rejectWithValue }) => {
    try {
      const response = await authService.login(credentials);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Login failed');
    }
  }
);

export const loginUserAs = createAsyncThunk(
  'auth/login',
  async (credentials: LoginCredentials, { rejectWithValue }) => {
    try {
      const response = await authService.loginUserAs(credentials);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Login failed');
    }
  }
);

export const activateAccount = createAsyncThunk(
  'auth/activateAccount',
  async (token: string, { rejectWithValue }) => {
    try {
      const response = await authService.activateAccount(token);

      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Account activation failed');
    }
  }
);

export const teamLoginUser = createAsyncThunk(
  'auth/teamLogin',
  async ({ teamId, credentials }: { teamId: string; credentials: LoginCredentials }, { rejectWithValue }) => {
    try {
      const response = await teamUserService.teamLogin(teamId, credentials);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Team login failed');
    }
  }
);

export const registerUser = createAsyncThunk(
  'auth/register',
  async (credentials: RegisterCredentials, { rejectWithValue }) => {
    try {
      const response = await authService.register(credentials);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Registration failed');
    }
  }
);

export const forgotPassword = createAsyncThunk(
  'auth/forgotPassword',
  async (request: ForgotPasswordRequest, { rejectWithValue }) => {
    try {
      await authService.forgotPassword(request);
      return true;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Forgot password failed');
    }
  }
);

export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async (request: ResetPasswordRequest, { rejectWithValue }) => {
    try {
      await authService.resetPassword(request);
      return true;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Reset password failed');
    }
  }
);

export const changePassword = createAsyncThunk(
  'auth/changePassword',
  async (request: ChangePasswordRequest, { rejectWithValue }) => {
    try {
      await authService.changePassword(request);
      return true;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Change password failed');
    }
  }
);

export const updateProfile = createAsyncThunk(
  'auth/updateProfile',
  async (request: UpdateProfileRequest, { rejectWithValue }) => {
    try {
      const response = await authService.updateProfile(request);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Update profile failed');
    }
  }
);

export const refreshToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.refreshToken();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Token refresh failed');
    }
  }
);

export const logoutUser = createAsyncThunk(
  'auth/logout',
  async (_, { dispatch }) => {
    try {
      // Call server logout endpoint
      await authService.authLogout();
    } catch (error) {
      console.warn('Server logout failed:', error);
    } finally {
      // Always clear local state regardless of server response
      dispatch(logout());
    }
  }
);

export const restoreAuth = createAsyncThunk(
  'auth/restoreAuth',
  async (_, { rejectWithValue, getState }) => {
    try {
      // Check if user has explicitly logged out in this session
      const loggedOutFlag = sessionStorage.getItem('user_logged_out');
      if (loggedOutFlag === 'true') {
        return { user: null, token: null, shouldClearAuth: true };
      }
      
      // Try to restore auth from cookies
      const authData = CookieUtils.getAuthData();
      const token = authData.token;
      const userData = authData.user;
      
      // Check for team user data in localStorage (team users store data there)
      const isTeamUser = localStorage.getItem('isTeamUser') === 'true';
      const teamUserDataStr = localStorage.getItem('teamUserData');
      const teamAuthStr = localStorage.getItem('team_auth');
      
      if (isTeamUser && (teamUserDataStr || teamAuthStr)) {
        try {
          // Prefer teamUserData, fall back to team_auth
          const teamUserData = teamUserDataStr 
            ? JSON.parse(teamUserDataStr)
            : JSON.parse(teamAuthStr!);
          return { user: teamUserData, token: token || null, shouldClearAuth: false };
        } catch {
          // Invalid team user data, continue with regular auth check
        }
      }
      
      // If we have user data in cookies, restore it
      if (userData) {
        const restoredUser = {
          ...userData,
          role: userData.role ?? 'merchant',
          isTeamUser: userData.isTeamUser ?? false,
        };
        return { user: restoredUser, token: token || null, shouldClearAuth: false };
      }
      
      // If we have a token cookie but no user data, user is still authenticated
      // (server will validate the session cookie)
      if (token) {
        return { user: null, token: token, shouldClearAuth: false };
      }
      
      // No auth data found - user is not authenticated
      return { user: null, token: null, shouldClearAuth: true };
    } catch (error: any) {
      // For any errors, don't clear auth state - might be temporary issue
      return rejectWithValue(error.message || 'Failed to restore auth');
    }
  }
);

// Auth slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    logout: (state) => {
      // Set logout flag in sessionStorage to prevent restore on refresh
      sessionStorage.setItem('user_logged_out', 'true');
      
      // Clear ONLY merchant auth cookies and localStorage
      clearAuthCookiesService.clearMerchantAuthData();
      
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.loading = false;
      state.error = null;
      state.loggedOut = true;
    },
    teamUserLogout: (state) => {
      // Set logout flag in sessionStorage to prevent restore on refresh
      sessionStorage.setItem('user_logged_out', 'true');
      
      // Clear ONLY team user cookies and localStorage
      clearAuthCookiesService.clearTeamUserAuthData();
      
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.loading = false;
      state.error = null;
      state.loggedOut = true;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.isAuthenticated = true;
        // Extract user data from response structure
        const userData = action.payload.data?.data ?? action.payload.data;
        const merchantUser = {
          ...userData,
          role: 'merchant',
          isTeamUser: false,
        };
        state.user = merchantUser;
        state.token = null; // Using cookies, no token in state
        state.error = null;
        state.loggedOut = false;
        
        // Clear logout flag and store user data in cookies for session persistence
        sessionStorage.removeItem('user_logged_out');
        CookieUtils.setUserData(merchantUser);
        
        const message = action.payload.data?.message || 'Login successful';
        toast.success(message);
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.error = action.payload as string;
        toast.error(action.payload as string || 'Login failed');
      })
      
      // Team Login
      .addCase(teamLoginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(teamLoginUser.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.isAuthenticated = true;
        // Extract user data from response structure
        const userData = action.payload.data?.data ?? action.payload.data;
        const teamUser = {
          ...userData,
          role: 'MERCHANT_TEAM_USER',
          isTeamUser: true,
          is_merchant: false, // Team users are not merchants
          // Ensure permission_list is always an array
          permission_list: userData.permission_list || [],
        };
        state.user = teamUser;
        state.token = null; // Using cookies, no token in state
        state.error = null;
        state.loggedOut = false;
        
        // Clear logout flag and store team login state
        sessionStorage.removeItem('user_logged_out');
        localStorage.setItem('isTeamUser', 'true');
        localStorage.setItem('teamUserData', JSON.stringify(teamUser));
        // Also store in team_auth key for easy access
        localStorage.setItem('team_auth', JSON.stringify({
          user_id: teamUser.user_id || teamUser.uuid,
          user_name: teamUser.user_name || teamUser.name,
          user_email: teamUser.user_email || teamUser.email,
          user_role: teamUser.user_role || teamUser.role,
          is_active: teamUser.is_active,
          is_merchant: false, // Team users are not merchants
          last_login: teamUser.last_login,
          permission_list: teamUser.permission_list || [],
        }));
        CookieUtils.setUserData(teamUser);
        
        const message = action.payload.data?.message || 'Team login successful';
        toast.success(message);
      })
      .addCase(teamLoginUser.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.error = action.payload as string;
        toast.error(action.payload as string || 'Team login failed');
      })
      
      // Register
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = null;
        // Registration doesn't automatically log in the user
        const message = action.payload.data?.message || 'Merchant registered successfully. Please check your email to activate your account.';
        toast.success(message);
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.error = action.payload as string;
        toast.error(action.payload as string || 'Registration failed');
      })
      
      // Activate Account
      .addCase(activateAccount.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(activateAccount.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = null;
        const message = action.payload.data?.message || 'Account activated successfully. You can now login.';
        toast.success(message);
      })
      .addCase(activateAccount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        toast.error(action.payload as string || 'Account activation failed');
      })
      
      // Forgot Password
      .addCase(forgotPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(forgotPassword.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = null;
        const message = action.payload?.data?.message || action.payload?.message || 'Password reset email sent';
        toast.success(message);
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        toast.error(action.payload as string || 'Failed to send password reset email');
      })
      
      // Reset Password
      .addCase(resetPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = null;
        const message = action.payload?.data?.message || action.payload?.message || 'Password reset successful';
        toast.success(message);
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        toast.error(action.payload as string || 'Failed to reset password');
      })
      
      // Change Password
      .addCase(changePassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(changePassword.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = null;
        const message = action.payload?.data?.message || action.payload?.message || 'Password changed successfully';
        toast.success(message);
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        toast.error(action.payload as string || 'Failed to change password');
      })
      
      // Update Profile
      .addCase(updateProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        // Update user data from response
        const userData = action.payload.data?.data ?? action.payload.data;
        const merchantUser = {
          ...userData,
          role: 'merchant',
          isTeamUser: false,
          is_merchant: true,
        };
        state.user = merchantUser;
        state.error = null;
        
        // Update user data in cookies for session persistence
        CookieUtils.setUserData(merchantUser);
        
        const message = action.payload.data?.message || 'Profile updated successfully';
        toast.success(message);
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        toast.error(action.payload as string || 'Failed to update profile');
      })
      
      // Refresh Token
      .addCase(refreshToken.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(refreshToken.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.token = null; // Using cookies, no token in state
        state.error = null;
      })
      .addCase(refreshToken.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.error = action.payload as string;
      })
      
      // Restore Auth
      .addCase(restoreAuth.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(restoreAuth.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        
        // Check if we should clear auth (user explicitly logged out)
        if (action.payload.shouldClearAuth) {
          state.isAuthenticated = false;
          state.user = null;
          state.token = null;
          state.error = null;
          state.loggedOut = true;
          return;
        }
        
        // If we have user data, restore the session
        if (action.payload.user) {
          state.isAuthenticated = true;
          // Ensure user has proper merchant/team user flags for UI rendering
          const userData = action.payload.user;
          const isTeamUser = userData.isTeamUser ?? false;
          const restoredUser = {
            ...userData,
            // Team users have is_merchant: false, regular merchants have is_merchant: true
            is_merchant: isTeamUser ? false : (userData.is_merchant ?? true),
            isTeamUser: isTeamUser,
            role: userData.role ?? (isTeamUser ? 'MERCHANT_TEAM_USER' : 'merchant'),
          };
          state.user = restoredUser;
          state.token = action.payload.token || null;
          state.error = null;
          state.loggedOut = false; // Reset logout flag on successful restore
          return;
        }
        
        // If we only have a token (no user data), user is still authenticated
        // The server cookie is valid, just need to mark as authenticated
        if (action.payload.token) {
          state.isAuthenticated = true;
          state.token = action.payload.token;
          state.error = null;
          state.loggedOut = false;
          return;
        }
        
        // No auth data at all - not authenticated
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.error = null;
      })
      .addCase(restoreAuth.rejected, (state, action) => {
        state.loading = false;
        // Don't clear auth state on rejection - might be a temporary network issue
        // Only clear if we explicitly got user: null in fulfilled case
        state.error = action.payload as string || null;
        // Keep existing auth state if restore fails due to network issues
      });
  },
});

export const { clearError, logout, teamUserLogout } = authSlice.actions;
export default authSlice;
